import React from 'react';

// ── Slack-style text formatter ──────────────────────────────────────────────
// Parses a subset of Slack mrkdwn into React nodes.
// See docs/notifications.md for the full spec.
//
// Inline: *bold* _italic_ ~strike~ `code` <url|label> <url> <mailto:x|label>
// Block:  • / - (unordered list), N. (ordered list), > (blockquote)

// ── Inline parsing ───────────────────────────────────────────────────────────

const LINK_RE = /^<(mailto:[^>|]+|https?:\/\/[^>|]+|[^>|]+)\|([^>]*)>/;
const BARE_LINK_RE = /^<(mailto:[^>]+|https?:\/\/[^>]+)>/;
const BOLD_RE = /^\*([^*]+)\*/;
const ITALIC_RE = /^_([^_]+)_/;
const STRIKE_RE = /^~([^~]+)~/;
const CODE_RE = /^`([^`]+)`/;

function parseInline(text, keyPrefix = '') {
  const nodes = [];
  let i = 0;
  let key = 0;

  while (i < text.length) {
    // Escaped character
    if (text[i] === '\\' && i + 1 < text.length) {
      nodes.push(text[i + 1]);
      i += 2;
      continue;
    }

    const rest = text.slice(i);

    // Link with label: <url|label>
    let m = rest.match(LINK_RE);
    if (m) {
      nodes.push(
        <a key={`${keyPrefix}-${key++}`} href={m[1]} target="_blank" rel="noopener noreferrer"
           className="text-brand-blue underline hover:text-brand-blue/80">
          {parseInline(m[2], `${keyPrefix}-${key}`)}
        </a>
      );
      i += m[0].length;
      continue;
    }

    // Bare link: <url>
    m = rest.match(BARE_LINK_RE);
    if (m) {
      nodes.push(
        <a key={`${keyPrefix}-${key++}`} href={m[1]} target="_blank" rel="noopener noreferrer"
           className="text-brand-blue underline hover:text-brand-blue/80">
          {m[1]}
        </a>
      );
      i += m[0].length;
      continue;
    }

    // Bold
    m = rest.match(BOLD_RE);
    if (m) {
      nodes.push(<strong key={`${keyPrefix}-${key++}`} className="font-bold text-ink">{m[1]}</strong>);
      i += m[0].length;
      continue;
    }

    // Italic
    m = rest.match(ITALIC_RE);
    if (m) {
      nodes.push(<em key={`${keyPrefix}-${key++}`}>{m[1]}</em>);
      i += m[0].length;
      continue;
    }

    // Strikethrough
    m = rest.match(STRIKE_RE);
    if (m) {
      nodes.push(<s key={`${keyPrefix}-${key++}`} className="text-ink-mute line-through">{m[1]}</s>);
      i += m[0].length;
      continue;
    }

    // Inline code
    m = rest.match(CODE_RE);
    if (m) {
      nodes.push(
        <code key={`${keyPrefix}-${key++}`} className="font-mono text-[11px] px-1 py-0.5 rounded bg-surface-3 text-brand-yellow">
          {m[1]}
        </code>
      );
      i += m[0].length;
      continue;
    }

    // Plain text: accumulate until next special char
    let next = i + 1;
    while (next < text.length && !'\\*_<~`'.includes(text[next])) {
      next++;
    }
    nodes.push(text.slice(i, next));
    i = next;
  }

  return nodes.length === 1 ? nodes[0] : nodes;
}

// ── Block parsing ────────────────────────────────────────────────────────────

const UL_RE = /^[•-]\s+(.*)/;
const OL_RE = /^\d+\.\s+(.*)/;
const QUOTE_RE = /^>\s?(.*)/;

/**
 * Parse a formatted message string into an array of React nodes.
 * Supports inline formatting plus block elements (lists, quotes).
 */
export function formatMessage(text) {
  if (!text) return null;

  const lines = text.split('\n');
  const blocks = [];
  let key = 0;

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Empty line — skip
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Unordered list
    if (UL_RE.test(line)) {
      const items = [];
      while (i < lines.length && UL_RE.test(lines[i])) {
        items.push(lines[i].replace(UL_RE, '$1'));
        i++;
      }
      const k = key++;
      blocks.push(
        <ul key={`blk-${k}`} className="list-none flex flex-col gap-0.5 my-1">
          {items.map((item, idx) => (
            <li key={idx} className="flex gap-1.5 text-[11px] text-ink-mute leading-relaxed">
              <span className="text-ink-mute shrink-0">•</span>
              <span>{parseInline(item, `ul-${k}-${idx}`)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (OL_RE.test(line)) {
      const items = [];
      while (i < lines.length && OL_RE.test(lines[i])) {
        items.push(lines[i].replace(OL_RE, '$1'));
        i++;
      }
      const k = key++;
      blocks.push(
        <ol key={`blk-${k}`} className="list-none flex flex-col gap-0.5 my-1">
          {items.map((item, idx) => (
            <li key={idx} className="flex gap-1.5 text-[11px] text-ink-mute leading-relaxed">
              <span className="text-ink-dim shrink-0">{idx + 1}.</span>
              <span>{parseInline(item, `ol-${k}-${idx}`)}</span>
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // Blockquote
    if (QUOTE_RE.test(line)) {
      const quoteLines = [];
      while (i < lines.length && QUOTE_RE.test(lines[i])) {
        quoteLines.push(lines[i].replace(QUOTE_RE, '$1'));
        i++;
      }
      const k = key++;
      blocks.push(
        <blockquote key={`blk-${k}`} className="border-l-2 border-surface-3 pl-2 my-1 text-[11px] text-ink-dim">
          {quoteLines.map((ql, idx) => (
            <div key={idx}>{parseInline(ql, `q-${k}-${idx}`)}</div>
          ))}
        </blockquote>
      );
      continue;
    }

    // Paragraph
    const paraLines = [];
    while (i < lines.length && lines[i].trim() !== '' &&
           !UL_RE.test(lines[i]) && !OL_RE.test(lines[i]) && !QUOTE_RE.test(lines[i])) {
      paraLines.push(lines[i]);
      i++;
    }
    const k = key++;
    blocks.push(
      <p key={`blk-${k}`} className="text-[11px] text-ink-mute leading-relaxed">
        {paraLines.map((pl, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <br />}
            {parseInline(pl, `p-${k}-${idx}`)}
          </React.Fragment>
        ))}
      </p>
    );
  }

  return blocks;
}

/**
 * Parse a single inline-formatted string (no block elements).
 * Used for fields table values and toast summaries.
 */
export function formatInline(text) {
  if (!text) return null;
  return parseInline(text, 'inline');
}
